import { useEffect, useRef, useState } from 'react';

/**
 * useLoadingVisibility — shared anti-flicker rules for loading indicators.
 *
 *   const visible = useLoadingVisibility(active, { delay, minVisible });
 *
 * - `active` flips true → wait `delay` ms before `visible` becomes true,
 *   so loads that finish quickly never show an indicator at all.
 * - Once visible, stays true for at least `minVisible` ms even if
 *   `active` drops, so an indicator that did appear never flashes.
 *
 * Used by LoadingState internally (delay half only — its parent unmounts
 * it, so it can't enforce min-visible on itself) and by gates that control
 * unmounting (ProjectWorkspace content slot, TopProgressBar), which apply
 * both rules and pass `delay={0}` to the LoadingState they render.
 */
export default function useLoadingVisibility(active, { delay = 200, minVisible = 400 } = {}) {
  const [visible, setVisible] = useState(active && delay <= 0);
  const shownAtRef = useRef(visible ? Date.now() : null);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (active) {
      if (shownAtRef.current != null) return undefined; // already visible
      if (delay <= 0) {
        shownAtRef.current = Date.now();
        setVisible(true);
        return undefined;
      }
      timerRef.current = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delay);
      return () => clearTimeout(timerRef.current);
    }

    // Deactivated. If never shown, just make sure the pending show is
    // cancelled (done above). If shown, hold until minVisible elapses.
    if (shownAtRef.current == null) return undefined;
    const elapsed = Date.now() - shownAtRef.current;
    const remaining = Math.max(0, minVisible - elapsed);
    if (remaining === 0) {
      shownAtRef.current = null;
      setVisible(false);
      return undefined;
    }
    timerRef.current = setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
    }, remaining);
    return () => clearTimeout(timerRef.current);
  }, [active, delay, minVisible]);

  return visible;
}

export { useLoadingVisibility };
